import type { FilmingSpecsBlock } from './types';

// Business-wide settings (final wave 1A). One row in app_settings, edited on
// /admin/settings. Every default equals the value that was hardcoded before
// settings existed, so with no saved row every output stays byte-identical.
//
// Phase 2 packages read this shape (2C brand, 2E release wording, 2G what
// happens next), so treat the top-level keys as stable: add fields, never
// rename or remove them.

export type WeatherMode = 'off' | 'free' | 'commercial';

export interface AppSettings {
  producer: { name: string; mobile: string; email: string };
  business: { brandName: string };
  newSheetDefaults: {
    crewRole: string;
    kit: string;
    filmingSpecs: Required<Pick<FilmingSpecsBlock, 'colourProfile' | 'frameRates' | 'resolution' | 'codec'>>;
    emergency: string;
  };
  /** Blank falls back to the GOOGLE_REVIEW_URL var. */
  links: { googleReviewUrl: string };
  /** Blank model falls back to ANTHROPIC_MODEL, then DEFAULT_AI_MODEL. */
  ai: { model: string; webSearch: boolean };
  /** apiKey is the one credential stored here: a low-risk paid Open-Meteo key
   *  for commercial mode. It never leaves the worker (see publicSettings). */
  weather: { mode: WeatherMode; apiKey: string };
  /** Read by 2E talent signing. Blank keeps signing off. */
  onCameraReleaseWording: string;
  /** Read by the 2G client day report. */
  whatHappensNext: string[];
}

/** Settings as the admin page sees them: the weather key is reduced to a hint. */
export type PublicAppSettings = Omit<AppSettings, 'weather'> & {
  weather: { mode: WeatherMode; hasKey: boolean; keyLast4: string };
};

/** Settings plus the revision loaded by the admin for optimistic saves. */
export type SettingsSnapshot = PublicAppSettings & { updatedAt: string | null };

/** The parts of settings that prompts and new-sheet defaults read. Both the
 *  worker's AppSettings and the admin page's PublicAppSettings satisfy it. */
export type ContentSettings = Pick<AppSettings, 'producer' | 'business' | 'newSheetDefaults'>;

export const DEFAULT_AI_MODEL = 'claude-sonnet-4-6';

export const DEFAULT_SETTINGS: AppSettings = {
  producer: { name: 'Henry Sheahan', mobile: '0428 070 068', email: 'henry@henrysheahanproductions.com' },
  business: { brandName: 'Sheahan Studios' },
  newSheetDefaults: {
    crewRole: 'Producer / DP',
    kit: 'Camera Kit (FX6 + A7C II), Lens Kit, Flowtech Tripod',
    filmingSpecs: {
      colourProfile: 'S-Cinetone (FX6) / S-Log3 (A7C II)',
      frameRates: '25p interviews, 50p B-roll',
      resolution: '4K',
      codec: 'XAVC-I',
    },
    emergency: 'In an emergency, dial 000.',
  },
  links: { googleReviewUrl: '' },
  ai: { model: '', webSearch: true },
  weather: { mode: 'free', apiKey: '' },
  // Starting wording from the approved mockup (M1 A1). Henry reviews both
  // before Phase 2 reads them; nothing uses them in Phase 1.
  onCameraReleaseWording: [
    'I agree that Sheahan Studios and its client for this shoot may film, photograph and record me on the day shown on this call sheet.',
    'They may use my image, voice and words, edited or not, in videos, photos and related material, in any media, worldwide and without time limit.',
    "I'm not owed any payment beyond what's already been agreed with me in writing. I understand I can ask for my contact details to be removed from Sheahan Studios' records at any time.",
  ].join('\n\n'),
  whatHappensNext: [
    'We back up and check all footage tonight.',
    'Your first edit arrives within 10 business days.',
    'You get two rounds of changes, then we deliver the final files.',
    'Final videos come with a short guide on where to use them.',
  ],
};

export type SettingsFieldErrors = Record<string, string>;
export type SettingsValidation = { ok: true; settings: AppSettings } | { ok: false; errors: SettingsFieldErrors };

export const MAX_STEPS = 30;
const MAX_TEXT = 2000;
const MAX_WORDING = 20000;
const MODEL_ID = /^[A-Za-z0-9][A-Za-z0-9._:@/-]{0,99}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Merge a partial update over `base`, trim strings and validate. Unknown keys
 * are dropped. Autosave sends one group at a time, so passing the saved
 * settings as `base` keeps every other field.
 *
 * Weather key rules: a blank or omitted `apiKey` keeps the saved key, and
 * `weather.clearKey: true` (write-only, never stored) removes it.
 */
export function validateSettings(input: unknown, base: AppSettings = DEFAULT_SETTINGS): SettingsValidation {
  const errors: SettingsFieldErrors = {};

  function merge(template: unknown, value: unknown, path: string): unknown {
    if (value === undefined) return structuredClone(template);
    if (typeof template === 'string') {
      if (typeof value !== 'string') { errors[path] = 'Enter text.'; return template; }
      const text = value.trim();
      if (text.length > (path === 'onCameraReleaseWording' ? MAX_WORDING : MAX_TEXT)) errors[path] = 'This is too long.';
      return text;
    }
    if (typeof template === 'boolean') {
      if (typeof value !== 'boolean') { errors[path] = 'Choose on or off.'; return template; }
      return value;
    }
    if (Array.isArray(template)) {
      const valid = Array.isArray(value) && value.length <= MAX_STEPS
        && value.every((step) => typeof step === 'string' && step.trim() && step.length <= MAX_TEXT);
      if (!valid) { errors[path] = `Use up to ${MAX_STEPS} steps, none of them empty.`; return template; }
      return (value as string[]).map((step) => step.trim());
    }
    if (!isRecord(value)) { errors[path || 'settings'] = 'Expected a group of settings.'; return template; }
    return Object.fromEntries(Object.entries(template as Record<string, unknown>)
      .map(([key, entry]) => [key, merge(entry, value[key], path ? `${path}.${key}` : key)]));
  }

  const settings = merge(base, input, '') as AppSettings;

  const required: Array<[string, string]> = [
    ['producer.name', settings.producer.name],
    ['producer.mobile', settings.producer.mobile],
    ['business.brandName', settings.business.brandName],
  ];
  for (const [path, value] of required) if (!value && !errors[path]) errors[path] = "This can't be empty.";
  if (!errors['producer.email'] && !EMAIL.test(settings.producer.email)) errors['producer.email'] = 'Enter a valid email address.';
  if (!errors['links.googleReviewUrl'] && settings.links.googleReviewUrl) {
    try {
      const url = new URL(settings.links.googleReviewUrl);
      if (url.protocol !== 'https:') throw new Error('protocol');
    } catch { errors['links.googleReviewUrl'] = 'Enter a full link starting with https://'; }
  }
  if (!errors['ai.model'] && settings.ai.model && !MODEL_ID.test(settings.ai.model)) {
    errors['ai.model'] = 'Enter a model ID like claude-sonnet-4-6, or leave it empty.';
  }
  if (!['off', 'free', 'commercial'].includes(settings.weather.mode)) errors['weather.mode'] = 'Choose Off, free or commercial.';

  const weatherInput = isRecord(input) && isRecord(input.weather) ? input.weather : undefined;
  if (!settings.weather.apiKey) settings.weather.apiKey = base.weather.apiKey;
  if (weatherInput?.clearKey !== undefined && typeof weatherInput.clearKey !== 'boolean') errors['weather.clearKey'] = 'Choose true or false.';
  if (weatherInput?.clearKey === true) settings.weather.apiKey = '';
  if (settings.weather.mode === 'commercial' && !settings.weather.apiKey && !errors['weather.apiKey']) {
    errors['weather.apiKey'] = 'Paste your Open-Meteo API key to use commercial mode.';
  }

  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, settings };
}

/** What GET and PUT return. The weather key never leaves the worker. */
export function publicSettings(settings: AppSettings): PublicAppSettings {
  const { apiKey, mode } = settings.weather;
  return { ...structuredClone(settings), weather: { mode, hasKey: Boolean(apiKey), keyLast4: apiKey.length >= 12 ? apiKey.slice(-4) : '' } };
}
